import {
  BoxV2 as Box,
  Button,
  DropdownV2 as Dropdown,
  Flex,
  TextInput,
} from 'portal-commons';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';
import { useController, useForm } from 'react-hook-form';

import { isRequired, isValidEmail } from './validators';
import { createMaxLengthValidator, createUrlValidator } from '../../validators';
import {
  Brand,
  VettingClass,
  VettingProvider,
  brandController,
} from '@/apis/core';
import { Modal } from '@/components';
import { toastFlashMessage } from '@/utils';

interface RcsVetModalProps {
  open?: boolean;
  brand?: Brand;
  vettingClasses?: VettingClass[];
  vettingProviders?: VettingProvider[];
  onCancel?: () => void;
  onSubmit?: () => void;
}

interface FormValues {
  vettingClass: string;
  vettingProvider: string;
  businessContactEmail: string;
  website: string;
}

const Title: FunctionComponent = ({ children }) => (
  <Box
    sx={{
      paddingTop: 's',
      paddingBottom: 's',
      color: 't.black80',
      fontWeight: 600,
      fontSize: 'H600',
      lineHeight: 'normal',
    }}
  >
    {children}
  </Box>
);

const Message: FunctionComponent = ({ children }) => (
  <Box
    sx={{
      paddingTop: 'xs',
      paddingBottom: 's',
      color: 't.black80',
      fontWeight: 400,
      fontSize: 'H400',
      lineHeight: 'normal',
    }}
  >
    {children}
  </Box>
);

const Note: FunctionComponent = ({ children }) => (
  <Box
    sx={{
      width: '100%',
      color: 't.grey500',
      fontWeight: 400,
      fontSize: 'H100',
      lineHeight: 'normal',
    }}
  >
    {children}
  </Box>
);

const Inputs: FunctionComponent = ({ children }) => (
  <Flex
    sx={{
      flexDirection: 'column',
      gap: 'l',
      width: '100%',
      paddingTop: 'xs',
      paddingBottom: 's',
    }}
  >
    {children}
  </Flex>
);

const Price: FunctionComponent = ({ children }) => (
  <Box
    sx={{
      color: 't.black80',
      fontWeight: 400,
      fontSize: 'H500',
      lineHeight: 'normal',
    }}
  >
    {children}
  </Box>
);

const Actions: FunctionComponent = ({ children }) => (
  <Flex
    sx={{
      flexDirection: 'row',
      alignItems: 'center',
      gap: 's',
      paddingTop: 'xl',
      paddingBottom: 'xxxl',
    }}
  >
    {children}
  </Flex>
);

const BrandVetModal: FunctionComponent<RcsVetModalProps> = ({
  open,
  brand,
  vettingClasses = [],
  vettingProviders = [],
  onCancel,
  onSubmit,
}) => {
  const {
    control,
    formState: { errors },
    reset,
    handleSubmit,
  } = useForm<FormValues>();
  const {
    field: { value: vettingClass, onChange: setVettingClass },
    fieldState: { error: vettingClassError },
  } = useController({
    control,
    name: 'vettingClass',
    rules: {
      validate: {
        isRequired,
      },
    },
  });
  const {
    field: { value: vettingProvider, onChange: setVettingProvider },
    fieldState: { error: vettingProviderError },
  } = useController({
    control,
    name: 'vettingProvider',
    rules: {
      validate: {
        isRequired,
      },
    },
  });
  const {
    field: {
      value: businessContactEmail,
      onChange: setBusinessContactEmail,
      onBlur: validateBusinessContactEmail,
    },
    fieldState: { error: businessContactEmailError },
  } = useController({
    control,
    name: 'businessContactEmail',
    disabled: !!brand?.businessContactEmail,
    rules: {
      validate: {
        isRequired,
        isValidEmail,
        withValidMaxLength: createMaxLengthValidator(100),
      },
    },
  });
  const {
    field: { value: website, onChange: setWebsite, onBlur: validateWebsite },
    fieldState: { error: websiteError },
  } = useController({
    control,
    name: 'website',
    disabled: !!brand?.website,
    rules: {
      validate: {
        isRequired,
        isValidUrl: createUrlValidator('Invalid Website URL'),
        withValidMaxLength: createMaxLengthValidator(255),
      },
    },
  });
  const [proceeding, setProceeding] = useState(false);

  const vettingClassOptions = useMemo(
    () =>
      vettingClasses.map((vettingClass) => ({
        label: vettingClass.id,
        value: vettingClass.id,
      })),
    [vettingClasses]
  );
  const vettingProviderOptions = useMemo(
    () =>
      vettingProviders.map((vettingProvider) => ({
        label: vettingProvider.displayName,
        value: vettingProvider.uid,
      })),
    [vettingProviders]
  );

  const getPrice = () => {
    if (vettingClass === 'RCS' && vettingProvider === 'AEGIS') {
      return '$75.00';
    }
    return '--';
  };

  const handleValid = async () => {
    if (brand) {
      setProceeding(true);
      if (!brand.businessContactEmail || !brand.website) {
        const data = {
          ...(businessContactEmail ? { businessContactEmail } : {}),
          ...(website ? { website } : {}),
        };
        await brandController.updateBrand(brand.uid, data);
      }
      const vettingRecord = await brandController.applyVetting(
        brand.uid,
        vettingClass,
        vettingProvider
      );
      if (vettingRecord) {
        toastFlashMessage('RCS vet successfully submitted', 'success');
        onSubmit?.();
      }
      setProceeding(false);
    }
  };

  useEffect(() => {
    reset();
  }, [open]);

  return (
    <Modal open={open}>
      <form
        style={{ display: 'contents' }}
        noValidate
        onSubmit={(event) => {
          event.stopPropagation();
          handleSubmit(handleValid)(event);
        }}
      >
        <Title>Apply for New Vetting</Title>
        <Message>
          To register a RCS campaign you will need to initiate an RCS Vet for
          the brand.
          <br />
          <br />
          Select the Vetting Type from the drop-down menu. Once you have
          selected the type, select the vetting partner and click{' '}
          <span style={{ fontWeight: 800 }}>Submit</span>. By submitting the
          Brand for external vetting you accept the{' '}
          <a
            href="https://www.campaignregistry.com/TCR-T&Cs.pdf"
            target="_blank"
            rel="noreferrer"
          >
            Terms & Conditions
          </a>
          .
        </Message>
        <Note>* Indicates a Required Field</Note>
        <Inputs>
          <Dropdown
            required
            error={!!vettingClassError?.message}
            label="Vetting Type"
            helperText={vettingClassError?.message}
            options={vettingClassOptions}
            value={vettingClass}
            onChange={setVettingClass}
          />
          <Dropdown
            required
            error={!!vettingProviderError?.message}
            label="Vetting Partner"
            helperText={vettingProviderError?.message}
            options={vettingProviderOptions}
            value={vettingProvider}
            onChange={setVettingProvider}
          />
          {vettingClass === 'RCS' && !brand?.businessContactEmail && (
            <TextInput
              required
              error={!!businessContactEmailError?.message}
              label="Business Contact Email"
              note={businessContactEmailError?.message}
              maxLength={100}
              value={businessContactEmail}
              onChange={setBusinessContactEmail}
              onBlur={validateBusinessContactEmail}
            />
          )}
          {vettingClass === 'RCS' && !brand?.website && (
            <TextInput
              required
              error={!!websiteError?.message}
              label="Website URL"
              note={websiteError?.message}
              maxLength={255}
              value={website}
              onChange={setWebsite}
              onBlur={validateWebsite}
            />
          )}
        </Inputs>
        {vettingClass && vettingProvider && (
          <Price>
            <span style={{ fontWeight: 600 }}>Vetting Price</span>: {getPrice()}
          </Price>
        )}
        <Actions>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={
              proceeding ||
              Object.values(errors).some((error) => !!error.message)
            }
          >
            Submit
          </Button>
        </Actions>
      </form>
    </Modal>
  );
};

export default BrandVetModal;
